import {
  PaymentRequestForm,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { ALPHANUMERIC_MAX_CHARS } from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { useTranslation } from '@/i18n';
import { FormInput } from '@moneyforward/ap-frontend-components';
import { memo, useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

type Props = {
  index: number;
  property: TransactionProperty;
};

const InnerAlphanumericTypeInput = ({ index, property }: Props) => {
  const { control } = useFormContext<PaymentRequestForm>();
  const { placeholder, maxChars, minChars, id } = property;
  const { t } = useTranslation();
  const fieldName =
    `invoiceTransactions.${index}.custom_transaction_items.${id}` as const;

  const {
    field: { value, onChange: fieldOnChange },
  } = useController({
    name: fieldName,
    control,
    defaultValue: '',
  });

  const inputProps = useMemo(() => {
    return {
      placeholder: placeholder,
    };
  }, [placeholder]);

  const rules = useMemo(() => {
    return {
      maxLength: {
        value: maxChars ?? ALPHANUMERIC_MAX_CHARS,
        message: t('transaction_items.validation_message.alphanumeric.length', {
          maxLength: maxChars ?? ALPHANUMERIC_MAX_CHARS,
          minLength: minChars ?? 0,
        }),
      },
      minLength: {
        value: minChars ?? 0,
        message: t('transaction_items.validation_message.alphanumeric.length', {
          maxLength: maxChars ?? ALPHANUMERIC_MAX_CHARS,
          minLength: minChars ?? 0,
        }),
      },
      pattern: {
        value: /^[a-zA-Z0-9\sＡ-Ｚａ-ｚ０-９]*$/,
        message: t('transaction_items.validation_message.alphanumeric.pattern'),
      },
    };
  }, [maxChars, minChars, t]);

  const handleConvertFullWidthToHalfWidth = useCallback(() => {
    const currentValue = String(value || '');
    if (!currentValue) return;
    const convertedStr = currentValue
      .replace(/[０-９Ａ-Ｚａ-ｚ]/g, (match) => {
        return String.fromCharCode(match.charCodeAt(0) - 0xfee0);
      })
      .replace(/\u3000/g, ' ');
    fieldOnChange(convertedStr);
  }, [value, fieldOnChange]);

  return (
    <FormInput<PaymentRequestForm>
      control={control}
      name={fieldName}
      inputId={`${id}${index}`}
      inputProps={inputProps}
      onBlur={handleConvertFullWidthToHalfWidth}
      rules={rules}
    />
  );
};
InnerAlphanumericTypeInput.displayName = 'InnerAlphanumericTypeInput';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const AlphanumericTypeInput = memo(
  InnerAlphanumericTypeInput
) as typeof InnerAlphanumericTypeInput;
AlphanumericTypeInput.displayName = 'AlphanumericTypeInput';
